// OPTIONS (polish pass 2026-10-15; the accessibility review, R5: "no settings panel: no key remapping, no text size,
// no separate sound/music volumes, no motion option"). One small panel, reachable from the title menu and in game
// (the OPTIONS button, or O). Settings are a per-viewer convenience, so they live in this browser (localStorage) and
// the game works the same without them.
//   text size   scales every --fs-* token (and the in-world pixel text) 1x / 1.5x / 2x
//   music, sfx  separate volumes
//   motion      REDUCE stops screen shake, flashes and pulsing UI (defaults to the system's prefers-reduced-motion)
//   keys        every action rebinds to any key (by physical key code, so it works on any layout)
//   layout      (touch) the round buttons on the right and the walk stick on the left, or swapped (R5-010)

export type Action = 'up' | 'down' | 'left' | 'right' | 'attack' | 'use' | 'alt' | 'sound' | 'skill1' | 'skill2' | 'skill3' | 'skill4'
  | 'quick1' | 'quick2' | 'quick3' | 'quick4' | 'sneak' | 'dodge' | 'map' | 'sprint' | 'pack' | 'craft' | 'skills' | 'options' | 'heal';

const DEFAULT_KEYS: Record<Action, string[]> = {
  up: ['KeyW', 'ArrowUp'], down: ['KeyS', 'ArrowDown'], left: ['KeyA', 'ArrowLeft'], right: ['KeyD', 'ArrowRight'],
  attack: ['Space'], use: ['KeyE'], alt: ['KeyR'], sound: ['KeyM'],
  skill1: ['Digit1'], skill2: ['Digit2'], skill3: ['Digit3'], skill4: ['Digit4'],
  quick1: ['Digit5'], quick2: ['Digit6'], quick3: ['Digit7'], quick4: ['Digit8'],
  sneak: ['KeyV'], dodge: ['KeyQ'], map: ['KeyN'], sprint: ['ShiftLeft', 'ShiftRight'],
  pack: ['KeyI', 'KeyB'], craft: ['KeyC'], skills: ['KeyK'], options: ['KeyO'], heal: ['KeyH'],
};
const LABEL: Record<Action, string> = {
  up: 'Walk up', down: 'Walk down', left: 'Walk left', right: 'Walk right', attack: 'Swing', use: 'Use / talk / doors',
  alt: 'Search / board up / refuel', sound: 'Sound on/off', skill1: 'Skill 1', skill2: 'Skill 2', skill3: 'Skill 3', skill4: 'Skill 4',
  quick1: 'Quick item 1', quick2: 'Quick item 2', quick3: 'Quick item 3', quick4: 'Quick item 4', sneak: 'Sneak', dodge: 'Dodge',
  map: 'Big map', sprint: 'Sprint (hold)', pack: 'Pack', craft: 'Craft', skills: 'Skills', options: 'Options', heal: 'Heal (the best med on you)',
};

export interface Opts { text: number; music: number; sfx: number; reduceMotion: boolean; view: number; sprintToggle: boolean; sneakHold: boolean; padLeft: boolean; keys: Record<Action, string[]> }
function load(): Opts {
  const sys = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const o: Opts = { text: 1, music: 0.32, sfx: 0.55, reduceMotion: sys, view: 1, sprintToggle: false, sneakHold: false, padLeft: false, keys: structuredClone(DEFAULT_KEYS) };
  try { const s = JSON.parse(localStorage.getItem('dm_opts') ?? '{}'); Object.assign(o, s); o.keys = { ...structuredClone(DEFAULT_KEYS), ...(s.keys ?? {}) }; } catch { /* private mode */ }
  return o;
}
export const OPTS: Opts = load();
const listeners: (() => void)[] = [];
export function onOpts(f: () => void) { listeners.push(f); f(); }
function save() { try { localStorage.setItem('dm_opts', JSON.stringify(OPTS)); } catch { /* */ } apply(); for (const f of listeners) f(); }

/** the action a key event means, or '' */
export function actOf(e: KeyboardEvent): Action | '' {
  for (const [a, codes] of Object.entries(OPTS.keys) as [Action, string[]][]) if (codes.includes(e.code)) return a;
  return '';
}
export function keyName(a: Action): string { return (OPTS.keys[a] ?? []).map(prettyCode).join(' / ') || 'unbound'; }
function prettyCode(c: string) { return c.replace(/^Key/, '').replace(/^Digit/, '').replace(/^Arrow(.*)/, '$1 arrow').replace(/(Left|Right)$/, ' $1').replace('Space', 'Space'); }

/** text size and motion go on the document, so CSS does the rest */
function apply() {
  const r = document.documentElement;
  r.style.setProperty('--ts', String(OPTS.text));
  r.classList.toggle('ts-big', OPTS.text > 1);                    // a bigger text size: a phone drops its flourishes (style.css)
  r.classList.toggle('ts-huge', OPTS.text >= 2);                  // HUGE: a desktop's quick-slot pictures draw at 2x (style.css, R2-021)
  r.classList.toggle('reduce-motion', OPTS.reduceMotion);
  r.classList.toggle('pad-left', !!OPTS.padLeft);                 // R5-010: the round buttons on the left, the walk stick on the right (style.css)
}
apply();

let rebinding: Action | null = null;
export function openOptions(onClose?: () => void) {
  let el = document.getElementById('options');
  if (!el) { el = document.createElement('div'); el.id = 'options'; el.setAttribute('role', 'dialog'); el.setAttribute('aria-modal', 'true'); el.setAttribute('aria-label', 'Options'); document.body.appendChild(el); }
  const ctl = () => [...el!.querySelectorAll<HTMLElement>('button, input')];
  const render = () => {
    // R5-021: keep focus on the control you just used (it went back to the first after every choice and rebind)
    const at = ctl().indexOf(document.activeElement as HTMLElement);
    el!.innerHTML = '';
    const h = document.createElement('h3'); h.textContent = 'OPTIONS'; el!.appendChild(h);
    const row = (label: string, ctl: HTMLElement) => { const d = document.createElement('div'); d.className = 'op-row'; const l = document.createElement('label'); l.textContent = label; d.append(l, ctl); el!.appendChild(d); return d; };
    const seg = (vals: [number, string][], cur: number, set: (v: number) => void) => {
      const s = document.createElement('div'); s.className = 'op-seg';
      for (const [v, t] of vals) { const b = document.createElement('button'); b.type = 'button'; b.textContent = t; b.setAttribute('aria-pressed', String(v === cur)); b.className = v === cur ? 'on' : ''; b.onclick = () => { set(v); save(); render(); }; s.appendChild(b); }
      return s;
    };
    const slider = (cur: number, set: (v: number) => void) => { const i = document.createElement('input'); i.type = 'range'; i.min = '0'; i.max = '100'; i.value = String(Math.round(cur * 100)); i.oninput = () => { set(Number(i.value) / 100); save(); }; return i; };
    row('TEXT SIZE', seg([[1, 'NORMAL'], [1.5, 'LARGE'], [2, 'HUGE']], OPTS.text, v => OPTS.text = v));
    row('MUSIC', slider(OPTS.music, v => OPTS.music = v));
    row('EFFECTS', slider(OPTS.sfx, v => OPTS.sfx = v));
    // VIEW (R11-005: both sides of Whyte never fit): WIDE shows about a quarter more street; NORMAL keeps the big figures Matt asked for
    row('VIEW', seg([[1, 'NORMAL'], [1.25, 'WIDE']], OPTS.view, v => OPTS.view = v));
    row('MOTION', seg([[0, 'FULL'], [1, 'REDUCED']], OPTS.reduceMotion ? 1 : 0, v => OPTS.reduceMotion = v === 1));
    // R5-010: holding a key down for a long run, or a crouch, is hard for some hands; either can be a toggle (or a hold)
    if (!document.body.classList.contains('touch')) row('SPRINT', seg([[0, 'HOLD'], [1, 'TOGGLE']], OPTS.sprintToggle ? 1 : 0, v => OPTS.sprintToggle = v === 1));
    row('SNEAK', seg([[0, 'TOGGLE'], [1, 'HOLD']], OPTS.sneakHold ? 1 : 0, v => OPTS.sneakHold = v === 1));
    // R5-010: a one-hand or left-handed grip; the round buttons and the walk stick swap sides (a touch screen's controls)
    if (document.body.classList.contains('touch')) row('LAYOUT', seg([[0, 'BUTTONS RIGHT'], [1, 'BUTTONS LEFT']], OPTS.padLeft ? 1 : 0, v => OPTS.padLeft = v === 1));
    const kh = document.createElement('h4'); kh.textContent = 'KEYS (click one, then press the new key; Esc cancels)'; el!.appendChild(kh);
    const grid = document.createElement('div'); grid.className = 'op-keys';
    for (const a of Object.keys(LABEL) as Action[]) {
      const l = document.createElement('span'); l.textContent = LABEL[a];
      const b = document.createElement('button'); b.type = 'button'; b.textContent = rebinding === a ? 'PRESS A KEY...' : keyName(a);
      b.onclick = () => { rebinding = a; render(); };
      grid.append(l, b);
    }
    el!.appendChild(grid);
    if (note) { const n = document.createElement('p'); n.className = 'op-note'; n.setAttribute('role', 'status'); n.textContent = note; el!.appendChild(n); }
    const foot = document.createElement('div'); foot.className = 'op-foot';
    const reset = document.createElement('button'); reset.type = 'button'; reset.textContent = 'RESET KEYS'; reset.onclick = () => { OPTS.keys = structuredClone(DEFAULT_KEYS); save(); render(); };
    const done = document.createElement('button'); done.type = 'button'; done.textContent = 'DONE'; done.className = 'primary'; done.onclick = close;
    if (!document.body.classList.contains('touch')) foot.append(reset); foot.append(done); el!.appendChild(foot);   // no keys on a phone (R5-019)
    // open at the top with the first control focused (R5-017: it opened scrolled to the bottom on DONE)
    const first = el!.querySelector('button, input') as HTMLElement | null;
    if (at >= 0) ctl()[at]?.focus({ preventScroll: true });
    else if (rebinding === null && !note) { el!.scrollTop = 0; first?.focus(); }
  };
  // keys reach the panel's own buttons and sliders (Tab, Enter, Space, arrows: R5-015); only the game is kept from
  // seeing them, and while rebinding the next key is the new binding
  let note = '';
  const onKey = (e: KeyboardEvent) => {
    e.stopImmediatePropagation();
    if (rebinding) {
      e.preventDefault();
      if (e.code !== 'Escape' && e.code !== 'Tab') {
        // add the key to this action (keep one other), and say where it was taken from (R5-016)
        const from = (Object.keys(OPTS.keys) as Action[]).find(a => a !== rebinding && OPTS.keys[a].includes(e.code));
        if (from) OPTS.keys[from] = OPTS.keys[from].filter(c => c !== e.code);
        OPTS.keys[rebinding] = [e.code, ...OPTS.keys[rebinding].filter(c => c !== e.code)].slice(0, 2);
        note = from ? `${prettyCode(e.code)} moved from ${LABEL[from]} to ${LABEL[rebinding]}.` : `${LABEL[rebinding]}: ${keyName(rebinding)}.`;
        save();
      }
      rebinding = null; render(); return;
    }
    if (e.code === 'Escape' || (e.code === 'KeyO' && !(e.target instanceof HTMLInputElement))) { e.preventDefault(); close(); return; }
    // R5-021: Tab and Shift+Tab go round the panel's own controls, never out to the page behind it
    if (e.code === 'Tab') {
      const c = ctl(), i = c.indexOf(document.activeElement as HTMLElement);
      if (e.shiftKey && i <= 0) { e.preventDefault(); c[c.length - 1]?.focus(); }
      else if (!e.shiftKey && (i === -1 || i === c.length - 1)) { e.preventDefault(); c[0]?.focus(); }
    }
  };
  function close() { el!.hidden = true; removeEventListener('keydown', onKey, true); rebinding = null; onClose?.(); }
  el.hidden = false; render();
  addEventListener('keydown', onKey, true);
}
export const optionsOpen = () => { const el = document.getElementById('options'); return !!el && !el.hidden; };
